import { useEffect, useState } from "react";
import { formatDisplayName } from "../utils/displayName";
import AdminDashboardLayout from "../components/AdminDashboardLayout";
import AdminNotifications from "../components/AdminNotifications";
import ChangePasswordPanel from "../components/ChangePasswordPanel";
import PortalFeedback from "../components/PortalFeedback";
import { supabase } from "../supabaseClient";
import { getUserAccess } from "../utils/authRoles";
import { Eye, EyeOff, MailPlus, Pencil, RefreshCw, ShieldCheck, Trash2, UserCheck, UsersRound } from "lucide-react";

const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

export default function AdminProfile() {
  const [access, setAccess] = useState(null);
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamMessage, setTeamMessage] = useState("");
  const [teamError, setTeamError] = useState("");
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "scheduler",
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSavingId, setSignupSavingId] = useState("");
  const [signupMessage, setSignupMessage] = useState("");
  const [signupError, setSignupError] = useState("");
  const [signups, setSignups] = useState([]);
  const [showConfirmedSignups, setShowConfirmedSignups] = useState(false);

  const isMasterAdmin = access?.adminRole === "master_admin";

  const roleLabel = (role) =>
    ({
      master_admin: "Super Admin",
      admin: "Admin",
      scheduler: "Team",
    }[role] || role || "Admin");

  const displayMemberName = (member) => {
    const first = member.first_name || "";
    const last = member.last_name || "";
    const fullName = `${first} ${last}`.trim();
    return formatDisplayName(fullName) || member.email || "Admin user";
  };

  const displaySignupName = (signup) => {
    const fullName = `${signup.first_name || ""} ${signup.last_name || ""}`.trim();
    return formatDisplayName(fullName) || signup.email || "Portal user";
  };

  const formatDateTime = (value) => {
    if (!value) return "Not sent";
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDateParts = (value) => {
    if (!value) return { date: "Not sent", time: "" };
    const date = new Date(value);
    return {
      date: date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const waitingTime = (value) => {
    if (!value) return "";
    const diffMs = Date.now() - new Date(value).getTime();
    if (diffMs < 0) return "";
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return `${Math.max(minutes, 1)}m waiting`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return `${hours}h waiting`;
    return `${Math.floor(hours / 24)}d waiting`;
  };

  const fetchTeam = async () => {
    setTeamLoading(true);
    setTeamError("");

    const nowIso = new Date().toISOString();
    const [{ data: teamRows, error: teamErrorResult }, { data: inviteRows, error: inviteError }] =
      await Promise.all([
        supabase.rpc("list_admin_team"),
        supabase
          .from("admin_invites")
          .select("id, email, role, status, created_at, expires_at")
          .eq("status", "pending")
          .gt("expires_at", nowIso)
          .order("created_at", { ascending: false }),
      ]);

    if (teamErrorResult) {
      setMembers([]);
      setTeamError(teamErrorResult.message || "Could not load admin team.");
      setTeamLoading(false);
      return;
    }

    setMembers(teamRows || []);

    if (inviteError && isMasterAdmin) {
      setInvites([]);
      setTeamError(inviteError.message || "Could not load admin invites.");
    } else {
      setInvites(inviteRows || []);
    }

    setTeamLoading(false);
  };

  const fetchSignups = async (includeConfirmed = showConfirmedSignups) => {
    setSignupLoading(true);
    setSignupError("");

    const { data, error } = await supabase.rpc("list_user_signups", {
      include_confirmed: includeConfirmed,
    });

    if (error) {
      setSignups([]);
      setSignupError(error.message || "Could not load user signups.");
      setSignupLoading(false);
      return;
    }

    setSignups(data || []);
    setSignupLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const next = await getUserAccess();
      if (mounted) setAccess(next);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (access?.isAdmin) fetchTeam();
  }, [access?.isAdmin]);

  useEffect(() => {
    if (isMasterAdmin) fetchSignups(showConfirmedSignups);
  }, [isMasterAdmin, showConfirmedSignups]);

  useEffect(() => {
    const metadata = access?.user?.user_metadata || {};
    setProfileForm({
      first_name: metadata.first_name || metadata.firstName || "",
      last_name: metadata.last_name || metadata.lastName || "",
      phone: metadata.phone || "",
    });
  }, [access?.user?.id]);

  const handleProfileSave = async () => {
    if (!access?.user?.id) return;

    setProfileSaving(true);
    setProfileMessage("");
    setProfileError("");

    const firstName = profileForm.first_name.trim();
    const lastName = profileForm.last_name.trim();
    const phone = profileForm.phone.trim();

    const { data, error } = await supabase.auth.updateUser({
      data: {
        first_name: firstName || null,
        last_name: lastName || null,
        phone: phone || null,
      },
    });

    setProfileSaving(false);

    if (error) {
      setProfileError(error.message || "Could not save your admin profile.");
      return;
    }

    setAccess((current) =>
      current
        ? {
            ...current,
            user: data?.user || current.user,
          }
        : current
    );
    setProfileMessage("Saved.");
    setProfileEditing(false);
    await fetchTeam();
  };

  const resetProfileForm = () => {
    const metadata = access?.user?.user_metadata || {};
    setProfileForm({
      first_name: metadata.first_name || metadata.firstName || "",
      last_name: metadata.last_name || metadata.lastName || "",
      phone: metadata.phone || "",
    });
    setProfileError("");
    setProfileMessage("");
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    setTeamError("");
    setTeamMessage("");

    const email = inviteForm.email.trim().toLowerCase();
    if (!email) {
      setTeamError("Enter an email address to invite.");
      return;
    }

    setTeamSaving(true);
    const { data: invite, error } = await supabase.rpc("invite_admin_user", {
      target_email: email,
      target_role: inviteForm.role,
    });

    if (error) {
      setTeamError(error.message || "Could not create admin invite.");
      setTeamSaving(false);
      return;
    }

    const { error: sendError } = await supabase.functions.invoke("send-team-invite", {
      body: {
        type: "admin",
        invite_id: invite?.id,
      },
    });

    if (sendError) {
      setTeamError(sendError.message || "Invite was created, but the email could not be sent.");
      await fetchTeam();
      setTeamSaving(false);
      return;
    }

    setInviteForm({ email: "", role: "scheduler" });
    setTeamMessage(`Invite sent to ${email}.`);
    await fetchTeam();
    setTeamSaving(false);
  };

  const handleRoleChange = async (member, nextRole) => {
    if (member.role === nextRole) return;
    if (member.is_current_user) {
      setTeamError("You cannot change your own admin role here.");
      return;
    }

    setTeamSaving(true);
    setTeamError("");
    setTeamMessage("");

    const { error } = await supabase.rpc("set_admin_role", {
      target_user_id: member.user_id,
      target_role: nextRole,
    });

    if (error) {
      setTeamError(error.message || "Could not update admin role.");
      setTeamSaving(false);
      return;
    }

    setTeamMessage(`${member.email || "Admin user"} role updated.`);
    await fetchTeam();
    setTeamSaving(false);
  };

  const handleRemoveMember = async (member) => {
    if (member.is_current_user) {
      setTeamError("You cannot remove yourself from the admin team.");
      return;
    }

    const confirmed = window.confirm(`Remove ${member.email || "this admin user"} from admin access?`);
    if (!confirmed) return;

    setTeamSaving(true);
    setTeamError("");
    setTeamMessage("");

    const { error } = await supabase.rpc("remove_admin_user", {
      target_user_id: member.user_id,
    });

    if (error) {
      setTeamError(error.message || "Could not remove admin user.");
      setTeamSaving(false);
      return;
    }

    setTeamMessage(`${member.email || "Admin user"} removed.`);
    await fetchTeam();
    setTeamSaving(false);
  };

  const handleResendConfirmation = async (signup) => {
    if (!signup?.email || signup.email_confirmed_at) return;

    setSignupSavingId(`resend:${signup.user_id}`);
    setSignupError("");
    setSignupMessage("");

    const { error } = await supabase.auth.resend({
      type: "signup",
      email: signup.email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setSignupError(error.message || "Could not resend confirmation email.");
      setSignupSavingId("");
      return;
    }

    setSignupMessage(`Confirmation email resent to ${signup.email}.`);
    await fetchSignups(showConfirmedSignups);
    setSignupSavingId("");
  };

  const handleConfirmSignup = async (signup) => {
    if (!signup?.user_id || signup.email_confirmed_at) return;

    const confirmed = window.confirm(
      `Confirm ${signup.email} without email verification? Only do this after confirming the requester is legitimate.`
    );
    if (!confirmed) return;

    setSignupSavingId(`confirm:${signup.user_id}`);
    setSignupError("");
    setSignupMessage("");

    const { error } = await supabase.rpc("confirm_user_signup", {
      target_user_id: signup.user_id,
    });

    if (error) {
      setSignupError(error.message || "Could not confirm this user.");
      setSignupSavingId("");
      return;
    }

    const { error: noticeError } = await supabase.functions.invoke("send-account-confirmed", {
      body: {
        user_id: signup.user_id,
      },
    });

    if (noticeError) {
      setSignupMessage(`${signup.email} is confirmed and can log in, but the confirmation notice could not be sent.`);
      await fetchSignups(showConfirmedSignups);
      setSignupSavingId("");
      return;
    }

    setSignupMessage(`${signup.email} is confirmed and the account confirmation email was sent.`);
    await fetchSignups(showConfirmedSignups);
    setSignupSavingId("");
  };

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Admin Profile</h1>
          <p className="text-xs text-black/60 mt-1">Manage your Strobe admin access and account details.</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(420px,1.15fr)]">
          <div className="space-y-6">
            <div className={`${PANEL} px-4 sm:px-6 py-6 space-y-6`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight">Account Information</h2>
                  <p className="text-sm text-black/60">Update the name and phone number shown across your admin account.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (profileEditing) resetProfileForm();
                    setProfileEditing((current) => !current);
                  }}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-4 text-sm font-semibold text-black hover:bg-black/[0.03]"
                >
                  <Pencil className="h-4 w-4" />
                  {profileEditing ? "Cancel" : "Modify"}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input
                    type="text"
                    value={profileEditing ? profileForm.first_name : formatDisplayName(profileForm.first_name)}
                    readOnly={!profileEditing}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    className={`w-full rounded-md border border-black/10 px-3 py-2 text-sm ${profileEditing ? "bg-white" : "bg-white text-black/70"}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input
                    type="text"
                    value={profileEditing ? profileForm.last_name : formatDisplayName(profileForm.last_name)}
                    readOnly={!profileEditing}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    className={`w-full rounded-md border border-black/10 px-3 py-2 text-sm ${profileEditing ? "bg-white" : "bg-white text-black/70"}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone (optional)</label>
                  <input
                    type="text"
                    value={profileForm.phone}
                    readOnly={!profileEditing}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className={`w-full rounded-md border border-black/10 px-3 py-2 text-sm ${profileEditing ? "bg-white" : "bg-white text-black/70"}`}
                  />
                </div>
              </div>

	              <div className="pt-2">
	                {profileError && (
	                  <PortalFeedback type="error" className="mb-3">{profileError}</PortalFeedback>
	                )}
	                {profileMessage && (
	                  <PortalFeedback type="success" className="mb-3">{profileMessage}</PortalFeedback>
	                )}
                {profileEditing && (
                  <button
                    type="button"
                    onClick={handleProfileSave}
                    disabled={profileSaving}
                    className="w-full rounded-md bg-[#EB040D] px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {profileSaving ? "Saving..." : "Save Changes"}
                  </button>
                )}
              </div>
            </div>

	            <div className={`${PANEL} px-4 sm:px-6 py-6`}>
	              <ChangePasswordPanel />
	            </div>

	            <div className={`${PANEL} px-4 sm:px-6 py-6`}>
	              <h2 className="text-lg sm:text-xl font-bold tracking-tight">Notification Preferences</h2>
	              <p className="mt-1 text-sm text-black/60">
	                Choose how Strobe admins should be alerted about new broker requests and broker-side request changes.
	              </p>
	              <div className="mt-6">
	                <AdminNotifications panelClass="rounded-xl border border-black/10 bg-white shadow-sm" />
	              </div>
	            </div>

	          </div>

	          <div className="space-y-6 lg:self-start">
	          {isMasterAdmin && (
	          <div className={`${PANEL} px-4 sm:px-6 py-6 space-y-6`}>
	            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
	              <div>
	                <div className="flex items-center gap-2">
	                  <UserCheck className="h-5 w-5 text-[#EB040D]" />
	                  <h2 className="text-lg sm:text-xl font-bold tracking-tight">User Signup Management</h2>
	                </div>
	                <p className="mt-1 max-w-3xl text-sm text-black/60">
	                  Review broker portal users who are waiting on email confirmation.
	                </p>
	              </div>
		              <div className="flex shrink-0 flex-nowrap items-center gap-2">
	                <button
	                  type="button"
	                  onClick={() => setShowConfirmedSignups((current) => !current)}
	                  className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-3 text-sm font-medium hover:bg-black/[0.03]"
	                >
	                  {showConfirmedSignups ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
	                  {showConfirmedSignups ? "Hide confirmed" : "Show confirmed"}
	                </button>
		                <button
		                  type="button"
		                  onClick={() => fetchSignups(showConfirmedSignups)}
		                  disabled={signupLoading}
		                  className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-black/10 bg-white text-black/70 hover:bg-black/[0.03] disabled:opacity-60"
		                  aria-label="Refresh user signups"
		                  title="Refresh user signups"
		                >
		                  <RefreshCw className={`h-4 w-4 ${signupLoading ? "animate-spin" : ""}`} />
		                </button>
	              </div>
	            </div>

	            {(signupError || signupMessage) && (
	              <PortalFeedback type={signupError ? "error" : "success"}>
	                {signupError || signupMessage}
	              </PortalFeedback>
	            )}

		            <div className="overflow-hidden rounded-xl border border-black/10 bg-white/70">
		              <div className="hidden grid-cols-12 border-b border-black/10 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-black/45 sm:grid">
		                <div className="col-span-4">User</div>
		                <div className="col-span-2">Status</div>
		                <div className="col-span-2">Last Sent</div>
		                <div className="col-span-4 text-center">Actions</div>
		              </div>

	              {signupLoading ? (
	                <div className="px-4 py-5 text-sm text-black/60">Loading signups...</div>
	              ) : signups.length === 0 ? (
	                <div className="px-4 py-5 text-sm text-black/60">
	                  No users are waiting for confirmation.
	                </div>
	              ) : (
	                signups.map((signup) => {
		                  const isConfirmed = Boolean(signup.email_confirmed_at);
		                  const resendSaving = signupSavingId === `resend:${signup.user_id}`;
		                  const confirmSaving = signupSavingId === `confirm:${signup.user_id}`;
		                  const confirmationSent = formatDateParts(signup.confirmation_sent_at);

	                  return (
		                    <div key={signup.user_id} className="grid grid-cols-1 gap-4 border-b border-black/5 px-4 py-4 last:border-b-0 sm:grid-cols-12 sm:items-center sm:gap-2 sm:py-3">
		                      <div className="sm:col-span-4">
		                        <p className="text-sm font-semibold">{displaySignupName(signup)}</p>
		                        <p className="text-xs text-black/50">{signup.email}</p>
		                        <p className="mt-1 text-xs text-black/45">
		                          Signed up {formatDateTime(signup.created_at)}
		                        </p>
		                      </div>
		                      <div className="sm:col-span-2">
		                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-black/35 sm:hidden">Status</p>
		                        {isConfirmed ? (
		                          <span className="inline-flex rounded-full bg-black/[0.06] px-3 py-1 text-xs font-semibold text-black/70">
		                            Confirmed
		                          </span>
		                        ) : (
		                          <div className="flex items-center gap-3 sm:block sm:space-y-1">
		                            <span className="inline-flex rounded-full bg-[#EB040D]/10 px-3 py-1 text-xs font-semibold text-[#EB040D]">
		                              Waiting
		                            </span>
		                            <p className="text-xs text-black/45">{waitingTime(signup.created_at)}</p>
		                          </div>
		                        )}
		                      </div>
		                      <div className="text-xs text-black/55 sm:col-span-2">
		                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-black/35 sm:hidden">Last sent</p>
		                        <p>{confirmationSent.date}</p>
		                        {confirmationSent.time && <p>{confirmationSent.time}</p>}
		                      </div>
		                      <div className="grid grid-cols-2 gap-2 sm:col-span-4 sm:flex sm:flex-col sm:items-stretch sm:justify-center">
		                        <button
		                          type="button"
		                          onClick={() => handleResendConfirmation(signup)}
		                          disabled={isConfirmed || Boolean(signupSavingId)}
			                          className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-3 text-sm hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-40"
		                          title={isConfirmed ? "User is already confirmed" : "Resend confirmation email"}
		                        >
		                          {resendSaving ? "Sending..." : "Resend"}
	                        </button>
	                        {isMasterAdmin && (
	                          <button
		                            type="button"
		                            onClick={() => handleConfirmSignup(signup)}
		                            disabled={isConfirmed || Boolean(signupSavingId)}
			                            className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-[#EB040D]/25 bg-white px-3 text-sm font-semibold text-[#EB040D] hover:bg-[#EB040D]/5 disabled:cursor-not-allowed disabled:opacity-40"
		                            title={isConfirmed ? "User is already confirmed" : "Confirm user manually"}
		                          >
		                            {confirmSaving ? "Confirming..." : "Confirm"}
	                          </button>
	                        )}
	                      </div>
	                    </div>
	                  );
	                })
	              )}
	            </div>
	          </div>
	          )}

	          <div className={`${PANEL} px-4 sm:px-6 py-6 space-y-6`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <UsersRound className="h-5 w-5 text-[#EB040D]" />
                <h2 className="text-lg sm:text-xl font-bold tracking-tight">Team Management</h2>
              </div>
              <p className="mt-1 max-w-3xl text-sm text-black/60">
                Add Strobe team members who can manage broker requests. Admins can update requests, while team members are intended for assignment and status workflows.
              </p>
            </div>
	            <button
	              type="button"
	              onClick={fetchTeam}
	              disabled={teamLoading}
	              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-black/10 bg-white text-black/70 hover:bg-black/[0.03] disabled:opacity-60"
	              aria-label="Refresh admin team"
	              title="Refresh admin team"
	            >
	              <RefreshCw className={`h-4 w-4 ${teamLoading ? "animate-spin" : ""}`} />
	            </button>
          </div>

          <div className="rounded-xl border border-black/10 bg-white/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-black/45">Strobe Admin Team</p>
            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-lg font-semibold">{members.length} active {members.length === 1 ? "user" : "users"}</p>
                <p className="text-sm text-black/55">
                  {invites.length > 0
                    ? `${invites.length} pending invites`
                    : "No pending invites"}
                </p>
              </div>
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-black/[0.04] px-3 py-1 text-xs font-medium text-black/65">
                <ShieldCheck className="h-4 w-4 text-[#EB040D]" />
                Internal Strobe access
              </div>
            </div>
          </div>

          {isMasterAdmin ? (
            <form onSubmit={handleInvite} className="grid grid-cols-1 gap-3 rounded-xl border border-black/10 bg-white/70 p-4 lg:grid-cols-[minmax(0,1fr)_180px_auto]">
              <div>
                <label className="block text-sm font-medium mb-1">Invite by Email</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="team@strobeaviation.com"
                  className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
                >
                  <option value="scheduler">Team</option>
                  <option value="admin">Admin</option>
                  <option value="master_admin">Super Admin</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={teamSaving}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#EB040D] px-4 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 lg:w-auto"
                >
                  <MailPlus className="h-4 w-4" />
                  Invite
                </button>
              </div>
            </form>
          ) : (
            <div className="rounded-xl border border-black/10 bg-white/70 px-4 py-3 text-sm text-black/60">
              Only super admins can invite Strobe users or change admin roles.
            </div>
          )}

	          {(teamError || teamMessage) && (
	            <PortalFeedback type={teamError ? "error" : "success"}>
	              {teamError || teamMessage}
	            </PortalFeedback>
	          )}

          <div className="overflow-hidden rounded-xl border border-black/10 bg-white/70">
            <div className="grid grid-cols-12 border-b border-black/10 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-black/45">
              <div className="col-span-6">User</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>

            {teamLoading ? (
              <div className="px-4 py-5 text-sm text-black/60">Loading team…</div>
            ) : members.length === 0 ? (
              <div className="px-4 py-5 text-sm text-black/60">No admin users found.</div>
            ) : (
              members.map((member) => {
                const canRemoveMember =
                  !member.is_current_user &&
                  (isMasterAdmin || (access?.adminRole === "admin" && member.role === "scheduler"));
                const roleChangeLocked = !isMasterAdmin || member.is_current_user;
                return (
                  <div key={member.id} className="grid grid-cols-12 items-center gap-2 border-b border-black/5 px-4 py-3 last:border-b-0">
                    <div className="col-span-12 sm:col-span-6">
                      <p className="text-sm font-semibold">
                        {displayMemberName(member)}
                        {member.is_current_user ? <span className="ml-2 text-xs font-normal text-black/45">You</span> : null}
                      </p>
                      <p className="text-xs text-black/50">{member.email || member.user_id}</p>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      {member.role === "master_admin" ? (
                        <span className="inline-flex rounded-full bg-black/[0.06] px-3 py-1 text-xs font-semibold text-black/70">
                          {roleLabel(member.role)}
                        </span>
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                          disabled={roleChangeLocked || teamSaving}
                          className="w-full max-w-[160px] rounded-md border border-black/10 bg-white px-2 py-1.5 text-sm disabled:opacity-60"
                          aria-label={`Change role for ${member.email || "admin user"}`}
                        >
                          <option value="scheduler">Team</option>
                          <option value="admin">Admin</option>
                          <option value="master_admin">Super Admin</option>
                        </select>
                      )}
                    </div>
                    <div className="col-span-6 flex justify-end sm:col-span-3">
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member)}
                        disabled={!canRemoveMember || teamSaving}
                        className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-3 text-sm hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-40"
                        title={
                          member.is_current_user
                            ? "You cannot remove yourself"
                            : canRemoveMember
                              ? "Remove admin user"
                              : "Only team members can be removed by admins"
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {invites.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-white/70 p-4">
              <h3 className="text-sm font-semibold">Invites</h3>
              <div className="mt-3 space-y-2">
                {invites.map((invite) => (
                  <div key={invite.id} className="flex flex-col gap-1 rounded-md border border-black/5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium">{invite.email}</p>
                      <p className="text-xs text-black/50">{roleLabel(invite.role)} • {invite.status}</p>
                    </div>
                    <p className="text-xs text-black/45">
                      Expires {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "soon"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
          </div>
        </div>
      </div>
    </AdminDashboardLayout>
  );
}
